import React from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'sonner';
import { ECampaignStatus } from 'types/campaign';

import { Box } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import ArrowBackRoundedIcon from '@material-ui/icons/ArrowBackRounded';
import DeleteRoundedIcon from '@material-ui/icons/DeleteRounded';
import InfoRoundedIcon from '@material-ui/icons/InfoRounded';
import ScheduleRoundedIcon from '@material-ui/icons/ScheduleRounded';

import Button from 'creative-components/CustomButtons/Button';

import CampaignInfoCards from 'components/CampaignInfoCards/CampaignInfoCards';
import CampaignSteps from 'components/CampaignSteps/CampaignSteps';
import DashboardBubble from 'components/DashboardBubble/DashboardBubble';
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator';
import { useUserInfoContext } from 'components/UserInfoProvider/UserInfoProvider';

import { calculateCampaignDates } from 'utils/lib';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    marginBottom: 40,
  },
  touchPreviewMessageContainer: {
    boxSizing: 'border-box',
    minWidth: '600px',
    padding: '6px 32px',
    margin: '30px 0 40px',
  },
  spacer: {
    marginTop: '100px',
  },
}));

const CreateCampaignTouchEditing = ({
  draftCampaign,
  setDraftCampaign,
  confirmDraftDeletion,
  setStep,
  isAnnualBilling,
  handleSetAnnualBilling,
}) => {
  const classes = useStyles();
  const history = useHistory();
  const theme = useTheme();
  const { userInfo } = useUserInfoContext();
  const onSaveDraft = () => {
    // Return to campaigns
    history.push('/dashboard/campaigns');

    toast.success('Your campaign draft has been saved. You can return to this campaign to pick up where you left off in the future.');
  };

  const handleClickReview = () => {
    setStep(3);
  };

  if (!draftCampaign) return (<LoadingIndicator />);

  const { campaignStartDate, campaignEndDate } = calculateCampaignDates(draftCampaign.eventType);

  const campaignCosts = {
    ...draftCampaign.estimatedCosts,
    isAnnualBilling,
  };

  return (
    <div className={classes.root}>
      <CampaignInfoCards
        startDate={campaignStartDate}
        endDate={campaignEndDate}
        eventType={draftCampaign.eventType}
        campaignCosts={campaignCosts}
        valuesAreEstimates
        apnsTotalSelectedCount={draftCampaign.estimatedNumberOfApns}
        apnsFarmingStoppedCount={0}
        isCreating
        apnsTotalInAreaCount={undefined}
        setIsAnnualBilling={handleSetAnnualBilling}
      />

      <Box display="flex" flexDirection="row" justifyContent="space-between" sx={{ mt: 4 }}>
        <Button
          simple
          color="dark"
          onClick={() => setStep(1)}
        >
          <ArrowBackRoundedIcon />
          Back to Farm Selection
        </Button>

        <Button
          round
          color="primary"
          onClick={handleClickReview}
        >
          Next
        </Button>
      </Box>

      <div className={classes.touchPreviewMessageContainer}>
        <DashboardBubble
          variant="status"
          tone="info"
          icon={<InfoRoundedIcon />}
          title="Preview your marketing touches"
          content={(
            <>
              You can preview and edit your marketing touches here before starting your campaign. A random property address will be used for the data on your touch previews until your campaign starts.
              <br />
              <br />
              The final order of touches will be assigned depending on the date your campaign starts. Click the Review &amp; Start button when you're ready to confirm the campaign details and launch it.
            </>
          )}
        />
      </div>

      <CampaignSteps
        agent={userInfo}
        eventType={draftCampaign.eventType}
        initialTouchTriggers={draftCampaign.touchTriggers}
        campaignStartDate={null}
        campaignStatus={ECampaignStatus.Draft}
      />

      <div className={classes.spacer} />

      <Box display="flex" flexDirection="row" justifyContent="space-between">
        <Box display="flex" gridGap={18}>
          <Button
            round
            color="danger"
            onClick={confirmDraftDeletion}
          >
            <DeleteRoundedIcon />
            {draftCampaign.autoRenewalForCampaign ? 'Cancel Renewal' : 'Delete Draft'}
          </Button>
          <Button
            round
            color="dark"
            onClick={onSaveDraft}
          >
            <ScheduleRoundedIcon />
            Save Draft
          </Button>
        </Box>

        <Button
          round
          color="primary"
          onClick={handleClickReview}
        >
          Next
        </Button>
      </Box>

    </div>
  );
};

export default CreateCampaignTouchEditing;
